"use client"

import { useState, useEffect } from "react"
import { Upload, X, Search, File, Image as ImageIcon, Trash2, Check, Loader2, Plus, RefreshCcw, ShieldAlert } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { collection, query, orderBy, onSnapshot, getDocs, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { deleteMedia, deleteMediaPermanently } from "@/lib/media"
import { toast } from "sonner"
import UploadMediaModal from "./upload-media-modal"

interface MediaItem {
  id: string
  title: string
  url: string
  type: "image" | "document"
  folder: string
  createdAt: any
}

interface MediaLibraryProps {
  onSelect?: (media: MediaItem) => void
  allowSelection?: boolean
  initialTab?: "images" | "documents"
}

const MediaLibrary = ({ onSelect, allowSelection = false, initialTab = "images" }: MediaLibraryProps) => {
  const [activeTab, setActiveTab] = useState<"images" | "documents">(initialTab)
  const [media, setMedia] = useState<MediaItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      const collectionsToSync = ["courses", "articles", "magazines", "pyq", "staff", "testimonials"]
      let addedCount = 0

      // Get current media URLs to avoid duplicates
      const currentMediaSnap = await getDocs(collection(db, "media"))
      const existingUrls = new Set(currentMediaSnap.docs.map(doc => doc.data().url))

      for (const colName of collectionsToSync) {
        const snap = await getDocs(collection(db, colName))
        for (const docSnap of snap.docs) {
          const data = docSnap.data()
          const urlsToProcess: { url: string; title: string; type: "image" | "document" }[] = []

          if (colName === "courses" && data.image) urlsToProcess.push({ url: data.image, title: data.title + " Cover", type: "image" })
          if (colName === "articles" && data.image) urlsToProcess.push({ url: data.image, title: data.title + " Cover", type: "image" })
          if (colName === "magazines") {
            if (data.imageUrl) urlsToProcess.push({ url: data.imageUrl, title: data.title + " Cover", type: "image" })
            if (data.pdfUrl) urlsToProcess.push({ url: data.pdfUrl, title: data.title + " PDF", type: "document" })
          }
          if (colName === "pyq" && data.documentUrls) {
            data.documentUrls.forEach((u: string) => urlsToProcess.push({ url: u, title: data.title + " Document", type: "document" }))
          }
          if (colName === "staff" && data.imageUrl) urlsToProcess.push({ url: data.imageUrl, title: data.name + " Profile", type: "image" })
          if (colName === "testimonials" && data.imageUrl) urlsToProcess.push({ url: data.imageUrl, title: data.name + " Photo", type: "image" })

          for (const item of urlsToProcess) {
            if (item.url && !existingUrls.has(item.url)) {
              await addDoc(collection(db, "media"), {
                title: item.title,
                url: item.url,
                type: item.type,
                folder: item.type === "image" ? "images" : "documents",
                createdAt: serverTimestamp()
              })
              existingUrls.add(item.url)
              addedCount++
            }
          }
        }
      }

      // Also check faculty poster
      const posterSnap = await getDoc(doc(db, "settings", "faculty_poster"))
      if (posterSnap.exists()) {
        const posterUrl = posterSnap.data().url
        if (posterUrl && !existingUrls.has(posterUrl)) {
          await addDoc(collection(db, "media"), {
            title: "Faculty Poster",
            url: posterUrl,
            type: "image",
            folder: "images",
            createdAt: serverTimestamp()
          })
          addedCount++
        }
      }

      toast.success(`Sync complete! Added ${addedCount} legacy items to your library.`)
    } catch (error) {
      console.error(error)
      toast.error("Sync failed")
    } finally {
      setIsSyncing(false)
    }
  }

  useEffect(() => {
    const q = query(collection(db, "media"), orderBy("createdAt", "desc"))
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items: MediaItem[] = []
      snapshot.forEach((doc) => {
        items.push({ id: doc.id, ...doc.data() } as MediaItem)
      })
      setMedia(items)
      setLoading(false)
    })
    return () => unsubscribe()
  }, [])

  // The handleUpload logic has been moved to UploadMediaModal

  const handleDelete = async (id: string, url: string) => {
    if (!confirm("Are you sure you want to delete this media from the library? (This will NOT remove it from Courses/Articles where it is used)")) return

    try {
      await deleteMedia(id, url)
      toast.success("Media deleted from library")
    } catch (error: any) {
      toast.error(error.message || "Delete failed")
    }
  }

  const handleDeepDelete = async (id: string, url: string) => {
    if (!confirm("WARNING: This will delete the image from the ENTIRE WEBSITE (Courses, Articles, etc.) and it cannot be recovered. Continue?")) return

    const toastId = toast.loading("Performing Deep Delete... Scanning all collections...")
    try {
      await deleteMediaPermanently(id, url)
      toast.success("Permanently deleted from entire website", { id: toastId })
    } catch (error: any) {
      toast.error(error.message || "Deep delete failed", { id: toastId })
    }
  }

  const filteredMedia = media.filter(
    (item) => item.type === (activeTab === "images" ? "image" : "document") &&
      item.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <>
      <div className="bg-background rounded-2xl border border-border/50 overflow-hidden flex flex-col h-[80vh]">
        {/* Header */}
        <div className="p-6 border-b border-border/50 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex gap-2 p-1 bg-bg-secondary rounded-lg">
            <button
              onClick={() => setActiveTab("images")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "images" ? "bg-background text-primary shadow-sm" : "text-text-secondary hover:text-text-primary"
                }`}
            >
              Images
            </button>
            <button
              onClick={() => setActiveTab("documents")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "documents" ? "bg-background text-primary shadow-sm" : "text-text-secondary hover:text-text-primary"
                }`}
            >
              Documents
            </button>
          </div>

          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={18} />
              <Input
                placeholder="Search media..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleSync}
                disabled={isSyncing}
              >
                <RefreshCcw size={18} className={isSyncing ? "animate-spin" : ""} />
                {isSyncing ? "Syncing..." : "Sync Library"}
              </Button>
              <Button
                variant="default"
                className="gap-2 relative overflow-hidden text-white"
                onClick={() => setIsUploadModalOpen(true)}
              >
                <Plus size={18} className="text-white" />
                Upload File
              </Button>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto p-6 bg-bg-secondary/20">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="animate-spin text-primary" size={32} />
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-text-secondary gap-6 p-8 border-2 border-dashed border-border/30 rounded-2xl bg-background/50">
              <div className="p-4 bg-bg-secondary rounded-full">
                <ImageIcon size={48} className="text-primary/40" />
              </div>
              <div className="text-center space-y-2 max-w-md">
                <h3 className="text-lg font-serif font-bold text-text-primary">Your Library is Waiting</h3>
                <p className="text-sm">If you have already uploaded images in Courses or Articles, click the <b>Sync Library</b> button above to bring them here automatically.</p>
              </div>
              <Button 
                onClick={handleSync} 
                disabled={isSyncing}
                className="gap-2 px-8 py-6 h-auto text-base font-bold uppercase tracking-widest shadow-xl shadow-primary/20"
              >
                <RefreshCcw size={20} className={isSyncing ? "animate-spin" : ""} />
                {isSyncing ? "Syncing your assets..." : "Import Existing Media Now"}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {filteredMedia.map((item) => (
                <div
                  key={item.id}
                  className="group relative aspect-square bg-background rounded-xl border border-border/50 overflow-hidden hover:border-primary/50 transition-all cursor-pointer"
                  onClick={() => allowSelection && onSelect?.(item)}
                >
                  {item.type === "image" ? (
                    <img
                      src={item.url}
                      alt={item.title}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-2 p-4">
                      <File size={48} className="text-primary/50" />
                      <span className="text-xs font-medium text-center line-clamp-2">{item.title}</span>
                    </div>
                  )}

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {allowSelection ? (
                      <div className="bg-primary text-white p-2 rounded-full">
                        <Check size={20} />
                      </div>
                    ) : (
                      <>
                        <Button 
                          size="icon" 
                          variant="destructive" 
                          className="bg-red-500 hover:bg-red-600 shadow-lg" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id, item.url);
                          }}
                          title="Delete from Library Only"
                        >
                          <Trash2 size={18} />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="destructive" 
                          className="bg-slate-950 hover:bg-black border border-red-500/50 shadow-lg group/deep" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeepDelete(item.id, item.url);
                          }}
                          title="DELETE FROM ENTIRE WEBSITE (PERMANENT)"
                        >
                          <ShieldAlert size={18} className="text-red-500 group-hover/deep:scale-110 transition-transform" />
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Title badge */}
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-medium truncate">
                    {item.title}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <UploadMediaModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        defaultType={activeTab === "images" ? "image" : "document"}
      />
    </>
  )
}

export default MediaLibrary
